import { Lock, Plus, Unlink } from "lucide-react";
import { useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { toastApiError } from "@/shared/utils/toast";

import { useAccessCan } from "../../hooks/useAccessCan";
import { usePoliciesData } from "../../hooks/useAccessData";
import { usePolicyUsage } from "../../hooks/usePolicyUsage";
import type { AccessPolicy } from "../../types/access.types";
import { singleAllowedCell } from "../../utils/permissionCells";
import { SettingsSection } from "../SettingsSection";
import { AccessAreaCatalog } from "./AccessAreaCatalog";
import { AccessEffectBadge } from "./AccessEffectBadge";
import { AccessScopeBadge } from "./AccessScopeBadge";
import { PolicyEditorDrawer } from "./PolicyEditorDrawer";

/** A Coloca base policy that grants exactly one permission: read by area rather than as a card. */
const isBasicPolicy = (p: AccessPolicy) => !p.organization_id && singleAllowedCell([p]) !== null;

/**
 * "Policies" section of Access & permissions. A policy bundles `resource:action` statements under a single
 * effect; deny policies win over allow. Lists the organization's and the combined policies by effect, the ~86
 * one-permission base policies by area (`AccessAreaCatalog`), and opens the editor. Unlike roles, a policy
 * **cannot be deleted while a role links it** (backend returns 409) — so the editor disables delete when
 * "used by > 0" and also handles the 409 defensively. The "used by N roles" count is derived for free from
 * the already-loaded roles (each role carries its policies). Gating: read `policies:read`; create
 * `policies:write`; edit `policies:update`; delete `policies:delete`.
 */
export function PoliciesSection() {
  const { t } = useTranslations("settings");
  const { isInternalUser } = useAuth();
  const { canAll } = useAccessCan();

  const { policies, isLoading, hasNoAccess, load } = usePoliciesData();
  // "Used by N roles" and its two honesty flags. Reading the roles catalog directly here used to drop both,
  // so a catalog that was loading, denied or simply failed produced `usedBy: 0` for every policy — the same
  // shape as "no role links this", which is what an access review acts on.
  const { usedByCount, usageLoading, usageUnknown, reloadUsage } = usePolicyUsage();

  const [editor, setEditor] = useState<{ policy: AccessPolicy | null } | null>(null);

  const canCreate = canAll("policies:write");
  // `:write`, not `:update` — `scopes[]` carries RBAC atoms, and the guard collapses `update` to `write`.
  const canUpdate = canAll("policies:write");
  const canDelete = canAll("policies:delete");

  const policyEditable = (policy: AccessPolicy): boolean =>
    !policy.is_protected && (isInternalUser || !!policy.organization_id);

  const [query, setQuery] = useState("");
  const term = query.trim().toLowerCase();
  const matches = (p: AccessPolicy) =>
    !term || p.name.toLowerCase().includes(term) || (p.description?.toLowerCase().includes(term) ?? false);

  // The seed's one-permission policies are read by area instead of as one card each; the organization's own
  // policies, the ones that combine several permissions and every deny keep their cards.
  const basic = useMemo(() => policies.filter(isBasicPolicy), [policies]);
  const allow = useMemo(() => policies.filter((p) => p.effect === "allow" && !isBasicPolicy(p)), [policies]);
  const deny = useMemo(() => policies.filter((p) => p.effect === "deny"), [policies]);

  // A policy mutation also refreshes roles: roles embed their policies' statements, so a role's effective
  // matrix and "used by" counts would otherwise show a stale copy after an edit.
  const reload = () => {
    load().catch(toastApiError);
    reloadUsage().catch(toastApiError);
  };

  const editorPolicy = editor?.policy ?? null;
  const editorReadOnly = editorPolicy ? !canUpdate || !policyEditable(editorPolicy) : false;

  return (
    <SettingsSection
      id="politicas"
      eyebrow={t("access_eyebrow")}
      title={t("access_policies_title")}
      sub={t("access_policies_sub")}
      headerRight={
        canCreate ? (
          <ColocaButton variant="primary" size="sm" icon={Plus} onClick={() => setEditor({ policy: null })}>
            {t("access_policies_create")}
          </ColocaButton>
        ) : undefined
      }
    >
      {hasNoAccess ? (
        <ColocaEmpty
          surface="card"
          icon={Lock}
          title={t("access_no_access_title")}
          description={t("access_no_access_description")}
        />
      ) : isLoading || usageLoading ? (
        // Waits for the roles catalog too: painting the cards before it lands would flash "usada por 0
        // roles" on every one and then take it back.
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ColocaSkeleton className="h-32 w-full" />
          <ColocaSkeleton className="h-32 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {/* Said once, at the top, rather than as a tooltip on a disabled button nobody hovers. */}
          {usageUnknown && (
            <ColocaAlert variant="warning" icon={Unlink} text={t("access_policies_usage_unavailable")} />
          )}
          <ColocaSearchInput
            value={query}
            onValueChange={setQuery}
            placeholder={t("access_catalog_search_placeholder")}
          />
          <PolicyGroup
            title={t("access_policies_group_allow")}
            count={allow.length}
            note={t("access_policies_group_allow_note")}
            policies={allow.filter(matches)}
            usedByCount={usedByCount}
            usageUnknown={usageUnknown}
            onOpen={(policy) => setEditor({ policy })}
          />
          <PolicyGroup
            title={t("access_policies_group_deny")}
            count={deny.length}
            note={t("access_policies_group_deny_note")}
            danger
            policies={deny.filter(matches)}
            usedByCount={usedByCount}
            usageUnknown={usageUnknown}
            onOpen={(policy) => setEditor({ policy })}
          />
          {basic.length > 0 && (
            <div>
              <div className="mb-2.5 flex flex-wrap items-baseline gap-2 border-b border-dashed border-border pb-2">
                <span className="h-2 w-2 self-center rounded-sm bg-primary" aria-hidden />
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-foreground">
                  {t("access_policies_group_basic")}
                </span>
                <ColocaBadge variant="secondary">{basic.length}</ColocaBadge>
                <span className="ml-auto text-[11.5px] text-muted-foreground">
                  {t("access_policies_group_basic_note")}
                </span>
              </div>
              <AccessAreaCatalog
                items={basic}
                cellOf={(policy) => singleAllowedCell([policy])}
                getId={(policy) => policy.id}
                describe={(policy) =>
                  usageUnknown
                    ? policy.name
                    : `${policy.name} · ${
                        usedByCount(policy.id) === 1
                          ? t("access_policy_used_by_singular")
                          : t("access_policy_used_by_plural", { count: usedByCount(policy.id) })
                      }`
                }
                onOpen={(policy) => setEditor({ policy })}
                query={query}
              />
            </div>
          )}
        </div>
      )}

      <PolicyEditorDrawer
        open={!!editor}
        onOpenChange={(open) => {
          if (!open) setEditor(null);
        }}
        policy={editorPolicy}
        readOnly={editorReadOnly}
        usedByCount={editorPolicy ? usedByCount(editorPolicy.id) : 0}
        // Fails closed on `usageUnknown`: with the roles catalog unread, every policy reports 0 links and the
        // drawer would offer a delete the backend answers with 409.
        canDelete={editorPolicy ? canDelete && policyEditable(editorPolicy) && !usageUnknown : false}
        onSaved={() => {
          setEditor(null);
          reload();
        }}
        onDeleted={() => {
          setEditor(null);
          reload();
        }}
      />
    </SettingsSection>
  );
}

interface PolicyGroupProps {
  title: string;
  count: number;
  note: string;
  danger?: boolean;
  policies: readonly AccessPolicy[];
  usedByCount: (policyId: string) => number;
  /** The roles catalog could not be read, so no policy's usage is known. */
  usageUnknown: boolean;
  onOpen: (policy: AccessPolicy) => void;
}

function PolicyGroup({
  title,
  count,
  note,
  danger,
  policies,
  usedByCount,
  usageUnknown,
  onOpen,
}: PolicyGroupProps) {
  const { t } = useTranslations("settings");

  return (
    <div>
      <div className="mb-2.5 flex flex-wrap items-baseline gap-2 border-b border-dashed border-border pb-2">
        <span
          className={cn("h-2 w-2 self-center rounded-sm", danger ? "bg-destructive" : "bg-primary")}
          aria-hidden
        />
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-foreground">
          {title}
        </span>
        <ColocaBadge variant="secondary">{count}</ColocaBadge>
        <span className="ml-auto text-[11.5px] text-muted-foreground">{note}</span>
      </div>
      {policies.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-5 text-center text-[12.5px] text-muted-foreground">
          {t("access_policies_empty_group")}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {policies.map((policy) => (
            <PolicyCard
              key={policy.id}
              policy={policy}
              usedBy={usedByCount(policy.id)}
              usageUnknown={usageUnknown}
              onOpen={() => onOpen(policy)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PolicyCard({
  policy,
  usedBy,
  usageUnknown,
  onOpen,
}: {
  policy: AccessPolicy;
  usedBy: number;
  usageUnknown: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslations("settings");
  const isDeny = policy.effect === "deny";
  const isLocked = policy.is_protected || !policy.organization_id;
  const resourceCount = new Set(policy.statements.map((s) => s.resource)).size;
  const visible = policy.statements.slice(0, 6);
  const extra = policy.statements.length - visible.length;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "relative flex flex-col gap-2.5 rounded-md border bg-card p-3.5 text-left transition-colors hover:shadow-xs",
        isDeny ? "border-destructive/30 hover:border-destructive/50" : "border-border hover:border-border/70",
      )}
    >
      <span
        className={cn(
          "absolute bottom-0 left-0 top-0 w-[3px] rounded-l-md",
          isDeny ? "bg-destructive" : "bg-primary",
        )}
        aria-hidden
      />
      <div className="flex items-center gap-2 pl-1.5">
        <span className="text-[13.5px] font-semibold text-foreground">{policy.name}</span>
        {isLocked && <Lock size={11} className="text-muted-foreground/70" />}
        <span className="ml-auto">
          <AccessEffectBadge effect={policy.effect} />
        </span>
      </div>

      <div className="flex items-center gap-2 pl-1.5">
        <AccessScopeBadge organizationId={policy.organization_id} />
        <span className="font-mono text-[10.5px] text-muted-foreground/70">
          {t("access_policy_stmt_summary", { permits: policy.statements.length, resources: resourceCount })}
        </span>
      </div>

      {policy.description && (
        <div className="min-h-[28px] pl-1.5 text-[11.5px] leading-snug text-muted-foreground">
          {policy.description}
        </div>
      )}

      <div className="flex min-h-[22px] flex-wrap gap-1 pl-1.5">
        {visible.map((s, i) => (
          <span
            key={`${s.resource}:${s.action}:${i}`}
            className={cn(
              "rounded-sm border px-1.5 py-px font-mono text-[9.5px]",
              isDeny
                ? "border-destructive/40 bg-destructive/10 text-destructive"
                : "border-primary/30 bg-primary/10 text-primary",
            )}
          >
            {s.resource}:{s.action}
          </span>
        ))}
        {extra > 0 && <ColocaBadge variant="secondary">+{extra}</ColocaBadge>}
      </div>

      <div className="border-t border-dashed border-border pt-2 pl-1.5">
        {/* `?`, never `0`: an unread catalog and "no role links this" are the same empty array, and they
            lead an access review to opposite conclusions. */}
        <span
          className="font-mono text-[10.5px] text-muted-foreground"
          title={usageUnknown ? t("access_policies_usage_unknown_title") : undefined}
        >
          {usageUnknown
            ? `${t("access_policies_col_used_by")}: ?`
            : usedBy === 1
              ? t("access_policy_used_by_singular")
              : t("access_policy_used_by_plural", { count: usedBy })}
        </span>
      </div>
    </button>
  );
}
